import CommonTable, { TableColumn } from '../common/CommonTable';
import ImagePreview from '../common/media/ImagePreview';
import Pagination from '../common/Pagination';
import Card from '../UI/Card';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import { Star } from 'lucide-react';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi } from '@/api/callApi';
import React, { useEffect, useState } from 'react';
import { checkIsExpire, GetFilterUrl, toFixedThree } from '@/utility/helper';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { useDebounce } from '@/hooks/useDebounce';
import PageHeading from '../common/PageHeading';
import FormField from '../UI/FormField';

interface ProductStates {
  isLoading: boolean;
  productStatus: string;
  products: IproductResponse[];
  pageInfo: PageInfo;
  selectedProductIds: Set<string | number>;
}

interface ProductTableProps {
  categoryId?: string;
  brandId?: string;
  onSelectProducts?: (selected: IproductResponse[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialSelectedId?: string;
}

const ProductTable: React.FC<ProductTableProps> = ({
  categoryId,
  brandId,
  onSelectProducts,
  selectionMode = 'multiple',
  initialSelectedId,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [singleSelectedId, setSingleSelectedId] = useState<
    string | number | null
  >(null);
  const [states, setStates] = useState<ProductStates>({
    isLoading: false,
    productStatus: 'active',
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedProductIds: new Set(),
  });

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.products);

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const handleSelectProduct = (product: IproductResponse) => {
    if (selectionMode === 'single') {
      setSingleSelectedId(product.id);

      onSelectProducts?.([product]);
    } else {
      const isSelected = selectedItems.has(product.id);
      handleSelectItem(product, !isSelected);
    }
  };

  const fetchProducts = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.productStatus,
        search: debounceValue,
        brandId: brandId,
        categoryIds: categoryId,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        products: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.productStatus,
    debounceValue,
  ]);

  useEffect(() => {
    if (selectionMode === 'multiple') {
      const selectedData = states.products.filter((p) =>
        selectedItems.has(p.id),
      );
      onSelectProducts?.(selectedData);
    }
    if (
      initialSelectedId &&
      selectionMode === 'single' &&
      states.products.length > 0
    ) {
      const selectedProduct = states.products.find(
        (p) => p.id === initialSelectedId,
      );
      if (selectedProduct) {
        setSingleSelectedId(selectedProduct.id);
        onSelectProducts?.([selectedProduct]);
      }
    }
  }, [selectedItems, states.products, selectionMode, initialSelectedId]);

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item.directImageUrl
          ? item.directImageUrl
          : item?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'ItemId',
      header: 'Item Id',
      render: (item) => <span>{item?.inventoryItemId}</span>,
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item?.averageRating || 0, 1)}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <>
      <Card>
        <PageHeading text="Products" />
        <div className="flex items-center gap-3 mb-3">
          <FormField
            placeholder="Search for the product.."
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <CommonTable
          columns={columns}
          data={states.products}
          showCheckbox={categoryId || brandId ? false : true}
          selectedItems={
            selectionMode === 'single'
              ? new Set(singleSelectedId ? [singleSelectedId] : [])
              : selectedItems
          }
          onSelectAll={
            selectionMode === 'multiple' ? handleSelectAll : undefined
          }
          onSelectItem={handleSelectProduct}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
          loading={states.isLoading}
          minHeight="min-h-44"
        />
        <Pagination
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
    </>
  );
};

export default ProductTable;
